// src/App.jsx
// Eventory SaaS - Main application routing

import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import NavigationBar from './components/Shared/NavigationBar';
import OfflineBanner from './components/Shared/OfflineBanner';
import LoadingOverlay from './components/Shared/LoadingOverlay';
import { FeedbackHost } from './components/Shared/feedback';
import './styles/figma-refresh.css';
import PlaceholderLanding from './components/Landing/PlaceholderLanding';
import DemoPage from './components/Landing/DemoPage';
import PrivacyPage from './components/Landing/PrivacyPage';
import TermsPage from './components/Landing/TermsPage';
import PublicPricingPage from './components/Landing/PublicPricingPage';
import LoginPage from './components/Shared/LoginPage';
import RegistrationPage from './components/Shared/RegistrationPage';
import ForgotPasswordPage from './components/Shared/ForgotPasswordPage';
import ResetPasswordPage from './components/Shared/ResetPasswordPage';
import PrivateRoute from './components/PrivateRoute';
import BillingOverlayManager from './components/BillingOverlayManager';
import PricingPage from './pages/PricingPage';
import AccountDashboard from './pages/AccountDashboard';
import AccountTeam from './pages/AccountTeam';
import AccountVendors from './pages/AccountVendors';
import MealInsights from './pages/MealInsights';
import VendorInsights from './pages/VendorInsights';

// Customer ordering (public)
import CustomerLandingPage from './components/CustomerOrderPage/CustomerLandingPage';
import CustomerOrderPage from './components/CustomerOrderPage/CustomerOrderPage';

// Lazy-loaded components
const IconGallery = lazy(() => import('./components/Landing/IconGallery'));
const OnboardingIntent = lazy(() => import('./components/Landing/OnboardingIntent'));
const AccessCodeEntry = lazy(() => import('./components/CustomerOrderPage/AccessCodeEntry'));
const PreOrderPage = lazy(() => import('./components/CustomerOrderPage/PreOrderPage'));
const UserDashboard = lazy(() => import('./components/Dashboard/UserDashboard'));
const EventDashboard = lazy(() => import('./components/Dashboard/EventDashboard'));
const JoinEvent = lazy(() => import('./components/Dashboard/JoinEvent'));

// Event-scoped dashboards
const KitchenDashboard = lazy(() => import('./components/KitchenDashboard/KitchenDashboard'));
const ServerDashboard = lazy(() => import('./components/ServerDashboard/ServerDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard/AdminDashboard'));
const AdminCatalog = lazy(() => import('./components/Admin/AdminCatalog'));

// Routes that should NOT show the global navigation bar. These are full app
// surfaces that ship their own header/back chrome (and often their own Eventory
// logo) — leaving the marketing NavigationBar on would stack TWO headers and a
// duplicated logo ("Eventory Eventory"), which founders flagged on the event page.
const HIDE_NAV_ROUTES = [
	'/order',
	'/customer/menu',
	'/pre-order',
	'/login',
	'/register',
	'/dashboard',
	'/account',
	'/event',
	'/admin',
	'/icons',
	'/welcome',
];

const ConditionalNav = () => {
	const location = useLocation();
	const shouldHideNav = HIDE_NAV_ROUTES.some(route => location.pathname.startsWith(route));

	if (shouldHideNav) {
		return null;
	}

	return <NavigationBar />;
};

// Snap to top whenever the route changes. Without this, navigating from
// the FAQ at the bottom of one page into another page leaves the new
// page scrolled to wherever the previous one was — looks broken.
const ScrollToTop = () => {
	const { pathname, hash } = useLocation();
	useEffect(() => {
		// If the URL has a hash (e.g. /#pricing-faq), let the browser
		// handle the anchor scroll instead of jumping to the top.
		if (hash) return;
		window.scrollTo(0, 0);
	}, [pathname, hash]);
	return null;
};

function App() {
	return (
		<Router>
			<ScrollToTop />
			<OfflineBanner />
			<FeedbackHost />
			<ConditionalNav />
			<BillingOverlayManager />
			<Suspense fallback={<LoadingOverlay show={true} message="Loading..." fullScreen />}>
				<Routes>
				{/* Public - Landing */}
				<Route path="/" element={<PlaceholderLanding />} />
				<Route path="/demo" element={<DemoPage />} />
				<Route path="/icons" element={<IconGallery />} />
				<Route path="/welcome" element={<PrivateRoute component={OnboardingIntent} />} />
				<Route path="/privacy" element={<PrivacyPage />} />
				<Route path="/terms" element={<TermsPage />} />
				{/* Public marketing pricing — accessible without login. The
				   authenticated in-account billing view lives at /account/billing. */}
				<Route path="/pricing" element={<PublicPricingPage />} />
				<Route path="/account/billing" element={<PrivateRoute component={PricingPage} />} />
				
				{/* Public - Auth */}
					<Route path="/login" element={<LoginPage />} />
					<Route path="/register" element={<RegistrationPage />} />
					<Route path="/forgot-password" element={<ForgotPasswordPage />} />
					<Route path="/reset-password" element={<ResetPasswordPage />} />
					
					{/* Public - Join Event via Invite */}
					<Route path="/join/:inviteCode" element={<JoinEvent />} />

					{/* Public - Customer Ordering */}
					<Route path="/order" element={<CustomerLandingPage />} />
					<Route path="/order/code" element={<AccessCodeEntry />} />
					{/* Deep link: geteventory.app/order/AB12CD — straight into that event's table picker */}
					<Route path="/order/:eventCode" element={<CustomerLandingPage />} />
					<Route path="/customer/menu" element={<CustomerOrderPage />} />
					<Route path="/pre-order" element={<PreOrderPage />} />

					{/* Authenticated - User Dashboard (list of events) */}
					<Route path="/dashboard" element={<PrivateRoute component={UserDashboard} />} />
					<Route path="/account" element={<PrivateRoute component={AccountDashboard} />} />
					<Route path="/account/team" element={<PrivateRoute component={AccountTeam} />} />
					<Route path="/account/vendors" element={<PrivateRoute component={AccountVendors} />} />
					<Route path="/account/meal-insights" element={<PrivateRoute component={MealInsights} />} />
					<Route path="/account/vendor-insights" element={<PrivateRoute component={VendorInsights} />} />
					
						{/* Authenticated - Event Dashboard (dynamic based on assignments) */}
						<Route path="/event/:eventId" element={<PrivateRoute component={EventDashboard} />} />
						<Route path="/event/:eventId/kitchen/:kitchenId" element={<PrivateRoute component={KitchenDashboard} />} />
						<Route path="/event/:eventId/server" element={<PrivateRoute component={ServerDashboard} />} />
						<Route path="/event/:eventId/*" element={<PrivateRoute component={EventDashboard} />} />

					<Route path="/admin/event/:eventId/*" element={<PrivateRoute component={AdminDashboard} />} />

					{/* System-admin: shared meal catalog (any authenticated user can browse, edits gated server-side by SYSTEM_CATALOG_ADMINS env) */}
					<Route path="/admin/catalog" element={<PrivateRoute component={AdminCatalog} />} />

					{/* 404 */}
					<Route path="*" element={
						<div style={{ textAlign: 'center', padding: '60px 20px' }}>
							<h2>Page Not Found</h2>
							<p>The page you're looking for doesn't exist.</p>
							<a href="/" style={{ color: '#007bff' }}>Go to Home</a>
						</div>
					} />
				</Routes>
			</Suspense>
		</Router>
	);
}

export default App;
